import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Plus, ArrowUpCircle, ArrowDownCircle, Wallet } from 'lucide-react';
import { bankAccountApi, investmentApi } from '../services/api';
import { BankAccount, Investment } from '../types';
import PeriodInput, { periodToStartIso, periodToEndIso, isoToPeriod } from '../components/PeriodInput';
import Alert from '../components/Alert';
import Modal from '../components/Modal';
import InvestmentFormModal from '../components/InvestmentFormModal';
import InvestmentEditModal from '../components/InvestmentEditModal';
import PageSuggestion from '../components/PageSuggestion';
import { formatDateAlt } from '../utils/dateUtils';

const InvestmentsList: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
    const [selectedAccountId, setSelectedAccountId] = useState<string>('');
    const defaultPeriod = useMemo(() => isoToPeriod(null), []);
    const [filterStartPeriod, setFilterStartPeriod] = useState<string>(defaultPeriod);
    const [filterEndPeriod, setFilterEndPeriod] = useState<string>(defaultPeriod);
    const [investments, setInvestments] = useState<Investment[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingInvestment, setEditingInvestment] = useState<Investment | null>(null);
    const [totalBalance, setTotalBalance] = useState<number | null>(null);
    const [balanceLoading, setBalanceLoading] = useState<boolean>(false);

    useEffect(() => {
        const loadAccounts = async () => {
            try {
                const res = await bankAccountApi.getAll();
                const accounts = res.data || [];
                // Filter only vault accounts (isVault === true)
                const vaultAccounts = accounts.filter(acc => acc.isVault === true);
                setBankAccounts(vaultAccounts);

                // Check if bankAccountId is provided in URL params
                const bankAccountIdParam = searchParams.get('bankAccountId');
                if (bankAccountIdParam && vaultAccounts.some(acc => acc.id === bankAccountIdParam)) {
                    setSelectedAccountId(bankAccountIdParam);
                } else if (vaultAccounts.length > 0) {
                    setSelectedAccountId(vaultAccounts[0].id);
                }
            } catch (e) {
                // silently ignore
            }
        };
        loadAccounts();
    }, [searchParams]);

    const toUtcBoundary = (iso: string, endOfDay: boolean) => {
        const d = new Date(iso);
        const y = d.getUTCFullYear();
        const m = d.getUTCMonth();
        const day = d.getUTCDate();
        const utc = endOfDay ? new Date(Date.UTC(y, m, day, 23, 59, 59, 999)) : new Date(Date.UTC(y, m, day, 0, 0, 0, 0));
        return utc.toISOString();
    };

    const fetchInvestments = async () => {
        if (!selectedAccountId) return;
        setLoading(true);
        setError(null);
        try {
            const startIso = periodToStartIso(filterStartPeriod);
            const endIso = periodToEndIso(filterEndPeriod);
            const params = {
                bankAccountId: selectedAccountId,
                occurredAtStart: toUtcBoundary(startIso, false),
                occurredAtEnd: toUtcBoundary(endIso, true),
            };
            const res = await investmentApi.getAll(params);
            setInvestments(res.data || []);
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Não foi possível carregar os investimentos.';
            setError(friendly);
            setInvestments([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchBalance = async () => {
        if (!selectedAccountId) return;
        setBalanceLoading(true);
        try {
            const res = await investmentApi.getBalance(selectedAccountId);
            setTotalBalance(res.data.balance);
        } catch (e) {
            setTotalBalance(null);
        } finally {
            setBalanceLoading(false);
        }
    };

    useEffect(() => {
        fetchInvestments();
        fetchBalance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccountId]);

    const handleFormSuccess = () => {
        setIsModalOpen(false);
        fetchInvestments();
        fetchBalance();
    };

    const handleEdit = (investment: Investment) => {
        setEditingInvestment(investment);
        setIsEditModalOpen(true);
    };

    const handleEditSuccess = () => {
        setIsEditModalOpen(false);
        setEditingInvestment(null);
        fetchInvestments();
        fetchBalance();
    };

    const selectedAccount = useMemo(() => {
        return bankAccounts.find(acc => acc.id === selectedAccountId);
    }, [bankAccounts, selectedAccountId]);

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-secondary-900">Investimentos</h1>
                    <p className="text-secondary-600">
                        {selectedAccount ? (
                            <>
                                Listagem de investimentos e resgates por conta -{' '}
                                <Link
                                    to={`/bank-accounts?search=${encodeURIComponent(selectedAccount.name)}`}
                                    className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium transition-colors"
                                >
                                    <Wallet className="w-4 h-4" />
                                    {selectedAccount.name}
                                </Link>
                            </>
                        ) : (
                            'Listagem de investimentos e resgates por conta'
                        )}
                    </p>
                    <PageSuggestion>
                        Acompanhe os aportes e resgates por conta para entender a evolução do valor investido e revisar movimentações antigas.
                    </PageSuggestion>
                </div>
                <button onClick={() => setIsModalOpen(true)} className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2">
                    <Plus className="w-5 h-5" />
                    Adicionar Investimento
                </button>
            </div>

            {error && (<Alert type="error" title="Não foi possível carregar" message={error} />)}

            {/* Total Balance Card */}
            {selectedAccountId && (
                <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="text-sm font-medium text-primary-100">Total Investido</div>
                            <div className="mt-2 text-2xl sm:text-3xl font-bold break-words">
                                {balanceLoading ? 'Carregando...' : totalBalance !== null
                                    ? (totalBalance / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                    : '—'}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg border border-secondary-200">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="w-full sm:w-auto sm:min-w-[160px]">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Conta Bancária</label>
                        <select className="input-field" value={selectedAccountId} onChange={(e) => setSelectedAccountId(e.target.value)}>
                            {bankAccounts.map(acc => (
                                <option key={acc.id} value={acc.id}>{acc.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[120px]">
                        <PeriodInput label="Data inicial (MM/AAAA)" name="occurredAtStart" value={filterStartPeriod} onChange={setFilterStartPeriod} compact />
                    </div>
                    <div className="w-full sm:w-auto sm:min-w-[120px]">
                        <PeriodInput label="Data final (MM/AAAA)" name="occurredAtEnd" value={filterEndPeriod} onChange={setFilterEndPeriod} compact />
                    </div>
                    <button onClick={fetchInvestments} className="btn-primary w-full sm:w-auto shrink-0 h-[42px]" disabled={loading}>Filtrar</button>
                </div>
            </div>

            {/* Listing Table */}
            {loading ? (
                <div className="flex items-center justify-center h-48">
                    <div className="text-secondary-600">Carregando investimentos...</div>
                </div>
            ) : investments.length === 0 ? (
                <div className="bg-white border border-secondary-200 rounded-lg p-8 text-center">
                    <p className="text-secondary-600">Nenhuma movimentação encontrada para o período selecionado.</p>
                </div>
            ) : (
                <div className="bg-white border border-secondary-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                    <table className="min-w-[760px] w-full divide-y divide-secondary-200">
                        <thead className="bg-secondary-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Data</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Tipo</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-secondary-700 uppercase tracking-wider">Valor</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-secondary-700 uppercase tracking-wider">Saldo Após</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-secondary-200">
                            {investments.map((it) => (
                                <tr key={it.id} className="hover:bg-secondary-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                                        {(() => {
                                            // Handle format "2025-11" (YYYY-MM) or full ISO date
                                            if (it.occurredAt.match(/^\d{4}-\d{2}$/)) {
                                                const [year, month] = it.occurredAt.split('-');
                                                const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
                                                return `${monthNames[parseInt(month) - 1]}/${year}`;
                                            }
                                            return formatDateAlt(it.occurredAt);
                                        })()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {it.type === 'credit' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                <ArrowUpCircle className="w-3.5 h-3.5" />
                                                Investimento
                                            </span>
                                        ) : it.type === 'debit' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                                <ArrowDownCircle className="w-3.5 h-3.5" />
                                                Resgate
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                <ArrowUpCircle className="w-3.5 h-3.5" />
                                                Rendimento
                                            </span>
                                        )}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${it.type === 'credit' || it.type === 'yield' ? 'text-green-700' : 'text-red-700'}`}>
                                        {(it.type === 'credit' || it.type === 'yield') ? '+' : '-'} {(it.amount / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-secondary-900">
                                        {typeof it.balanceAfter === 'number' ? (it.balanceAfter / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                        <button
                                            onClick={() => handleEdit(it)}
                                            className="text-blue-600 hover:text-blue-800 transition-colors font-medium"
                                            title="Editar investimento"
                                        >
                                            Editar
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            )}

            {/* Create Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Adicionar Investimento"
            >
                <InvestmentFormModal
                    onSuccess={handleFormSuccess}
                    onCancel={() => setIsModalOpen(false)}
                />
            </Modal>

            {/* Edit Modal */}
            {editingInvestment && (
                <Modal
                    isOpen={isEditModalOpen}
                    onClose={() => {
                        setIsEditModalOpen(false);
                        setEditingInvestment(null);
                    }}
                    title="Editar Investimento"
                >
                    <InvestmentEditModal
                        investment={editingInvestment}
                        onSuccess={handleEditSuccess}
                        onCancel={() => {
                            setIsEditModalOpen(false);
                            setEditingInvestment(null);
                        }}
                    />
                </Modal>
            )}
        </div>
    );
};

export default InvestmentsList;
